/** Звонки за день по номерам — ровно то, что отдаёт `GET /api/calls/report`. */
import { apiFetch, query } from "@/lib/api";

export type Group = "answered" | "short" | "long";
export type Outcome = "in_time" | "late" | "none";
/** Почему разговора нет: номер невалиден · пытались ≥ 3 раз · звонили без ответа ·
 * клиент звонил снова, опять пропуск · не звонили. Первые две — не «не перезвонили». */
export type NoneReason = "invalid" | "attempted" | "tried" | "recalled" | "never";
/** Причины, которые не входят в «не перезвонили» (владелец 26.09.26). */
export const NOT_COUNTED: NoneReason[] = ["invalid", "attempted"];
/** Шаг воронки для входящего звонка; `steps[0]` — первый звонок номера за день. */
export type Step = "answered" | "short" | Outcome | "invalid" | "attempted";

export type HistoryCall = {
  id: number;
  /** Id для карточки `/calls/<id>` (своя копия звонков); null — звонок ещё не доехал. */
  card_id: number | null;
  at: string;
  type_id: number;
  answered: boolean;
  wait: number;
  talk: number;
  user: string | null;
  lead_id: number | null;
  deal_id: number | null;
  /** Линия без лидов (курьерская): лида и не должно быть. */
  line_note: string | null;
  /** У непринятого входящего дня: кто из КЦ что делал, пока звонок шёл без ответа. */
  staff?: StaffAt[];
};

/** free — на линии и свободен (не взял) · busy — разговаривал · off — не на линии. */
/** `free` — не взял: звонок дошёл; `unreached` — на линии, но телефон и Bitrix молчали; `short` — звонили меньше порога. */
export type StaffState = "free" | "unreached" | "short" | "busy" | "off";
export type StaffAt = {
  user_id: number;
  name: string;
  state: StaffState;
  status: string;
  /** Из лога Voximplant, если звонок разобран: сколько очередь отдавала звонок свободному, звонил телефон и его ответ. */
  free_sec?: number;
  rang_sec?: number;
  sip?: string;
  busy_call: { kind: string; at: string } | null;
};

export type NumberRow = {
  phone: string;
  group: Group;
  calls: number;
  missed: number;
  max_wait: number;
  first_at: string;
  episode_from: string | null;
  on_shift: boolean | null;
  /** За пропуски эпизода: кто не взял (звонок дошёл), до кого не дошёл и кому звонили меньше порога. */
  free: string[] | null;
  unreached: string[] | null;
  short: string[] | null;
  base_at: string | null;
  callback_at: string | null;
  callback_min: number | null;
  callback_by: string | null;
  outcome: Outcome | null;
  none_reason: NoneReason | null;
  tried: boolean;
  /** Наши исходящие с начала эпизода пропуска до разговора включительно. */
  attempts: number;
  last_attempt_at: string | null;
  /** Последние лид и сделка Битрикса по звонкам номера. */
  lead_id: number | null;
  deal_id: number | null;
  /** Номер звонил на линию без лидов (курьерскую) — лида не будет намеренно. */
  line_note: string | null;
  steps: Step[];
  history: HistoryCall[];
};

/** Воронка: входящие → ждали > 20 с → ответили → перезвонили / нет. */
export type Funnel = {
  incoming: number;
  reachable: number;
  answered: number;
  called_back: number;
  called_back_in_time: number;
  not_called_back: number;
  /** Без разговора, но не «не перезвонили»: пытались ≥ 3 раз / невалидный номер. */
  attempted: number;
  invalid: number;
};

export type Cell = { in_time: number; late: number; none: number; tried: number };

export type CallsReport = {
  day: string;
  long_wait_sec: number;
  callback_min_norm: number;
  totals: { numbers: number; answered: number; short: number; long: number; calls: number; not_called_back: number; attempted: number; invalid: number };
  funnel: { calls: Funnel; numbers: Funnel };
  callbacks: Record<"short" | "long", Record<"on_shift" | "off_shift", Cell>>;
  callback_min: { median: number | null; mean: number | null };
  numbers: NumberRow[];
};

/** Карточка лида или сделки в Битриксе — так же ссылается legacy CRM. */
export const bitrixUrl = (kind: "lead" | "deal", id: number) => `https://unirenter.bitrix24.ru/crm/${kind}/details/${id}/`;

export function loadCalls(day: string): Promise<CallsReport> {
  return apiFetch<CallsReport>(`/api/calls/report${query({ date: day })}`);
}

/** Кто не взял трубку — `GET /api/calls/missed`: накопитель `call_missed` (фон разбирает логи Voximplant). */
/**
 * Пропущенный входящий дня. `next` — чем он кончился: первый звонок по тому же номеру за сутки
 * после него (`out` — перезвонили мы, `in` — клиент позвонил снова), null — никто не звонил.
 */
export type MissedNext = { kind: "out" | "in"; at: string; answered: boolean; duration: number; user: string };
export type MissedCall = {
  id: number;
  at: string;
  free: number;
  where: "phone" | "tab";
  /** `ask` — не взял, `unreached` — на линии, но звонок не дошёл. */
  kind: "ask" | "unreached";
  /** Ответ телефона: ring, offline, busy, decline, limit. */
  sip: string;
  phone: string;
  line: string;
  /** Учётка Bitrix сотрудника, который не взял: по ней детализация отбирается по строке таблицы. */
  user_id: number;
  next: MissedNext | null;
};
export type MissedRow = {
  user_id: number;
  /** Сотрудник CRM — для ссылки на смену; null — связи учёток нет. */
  crm_id: number | null;
  name: string;
  day: number;
  week: number;
  month: number;
  /** Принятые входящие рабочих часов за те же периоды — объём, на фоне которого «не взял». */
  took: { day: number; week: number; month: number };
  /** Был на линии и свободен, очередь отдавала звонок, но он не дошёл. */
  unreached: { day: number; week: number; month: number };
  calls: MissedCall[];
};
export type MissedRating = {
  day: string;
  threshold_sec: number;
  /** Сколько дней по выбранный день в колонках `week` и `month`. */
  periods: [number, number];
  /** С какого момента пропущенные разобраны; null — накопитель пуст. */
  since: string | null;
  calls: { total: number; kinds: { kind: string; label: string; calls: number }[] };
  rows: MissedRow[];
  /** Детализация вкладки «Кто не взял»: каждый непринятый звонок за 30 дней, новые сверху. */
  missed: MissedCall[];
};

export function loadMissedRating(day: string): Promise<MissedRating> {
  return apiFetch<MissedRating>(`/api/calls/missed${query({ date: day })}`);
}

/** Вкладка «Дни» — `GET /api/calls/days`: входящие (воронка «Неотвеченных») и исходящие по дням, неделям, месяцам, новые сверху. */
export type DaysGroup = "day" | "week" | "month";
/** Исходящие из своей копии звонков: попытки, номера, соединились (код 200), разговоры по расшифровке. */
export type Outgoing = { calls: number; phones: number; connected: number; talks: number; talk_phones: number; unknown: number };
/** Исходящие по виду (лид в работе / новый, сделка, прочие): звонков, разговоров и итог звонка. */
export type DialKind = "lead_work" | "lead_new" | "deal" | "rest";
export type DialCount = { calls: number; talk: number; zapis: number; deal: number; work: number; refuse: number };
export type Dial = Record<DialKind, DialCount>;
export type CallsDaysRow = { day: string; to: string; funnel: { calls: Funnel; numbers: Funnel }; outgoing: Outgoing; dial: Dial };
export type CallsDays = {
  from: string;
  to: string;
  group: DaysGroup;
  long_wait_sec: number;
  callback_min_norm: number;
  rows: CallsDaysRow[];
  total: { funnel: { calls: Funnel; numbers: Funnel }; outgoing: Outgoing; dial: Dial };
};

export function loadCallsDays(from: string, to: string, group: DaysGroup): Promise<CallsDays> {
  return apiFetch<CallsDays>(`/api/calls/days${query({ from, to, group })}`);
}

/** Звонок из своей копии `bitrix_call` — `GET /api/calls/list`, `GET /api/calls/by-object`. */
export type CallItem = {
  id: number;
  at: string;
  type_id: number;
  phone: string | null;
  user_id: number | null;
  user: string | null;
  /** Код 200 — соединились; был ли разговор с клиентом — `talk`. */
  answered: boolean;
  code: string;
  reason: string;
  duration: number;
  portal: string;
  line_note: string | null;
  entity: { type: string; id: number | null } | null;
  lead_id: number | null;
  deal_id: number | null;
  record: boolean;
  transcript: string | null;
  talk: "talk" | "none" | "unclear" | null;
  /** Статусы лида и сделки сейчас — из CRM. */
  lead_status: string | null;
  deal_status: string | null;
  /** Итог звонка — правки сделки/лида тем же сотрудником в окне звонка; null — сверять не с чем. */
  outcome: { changes: OutcomeChange[] } | null;
  /** Теги звонка (`call_tag`) — свои метки для разбора и прослушки. */
  tags: string[];
};

export type OutcomeChange = {
  object: ObjectKind;
  /** Поле CRM; `created` — объект создан в окне звонка. */
  field: string;
  label: string;
  /** Стадия/статус и следующая обработка — в строку; остальное — в детали. */
  main: boolean;
  old: string;
  new: string;
  at: string;
};

export type CallsList = {
  from: string;
  to: string;
  page: number;
  page_size: number;
  total: number;
  users: { id: number; name: string; calls: number }[];
  /** Все теги с числом звонков — варианты фильтра и подсказки. */
  tags: CallTagCount[];
  /** Счётчики фильтра итога по всему периоду; null — фильтр итога не включён. */
  changes: ChangeSummary | null;
  /** Фильтр дня обзвона (`dialing`): день и тип. */
  dialing?: { date: string; type_id: number | null; type: string | null };
  /** Выборка по ссылке из «Записей» (`zapis`): разговоры, после которых записали; `people` — число из таблицы. */
  zapis?: { method: string; label: string; user: string; user_name: string; from: string; to: string; people: number };
  /** Выборка по ссылке из «Активности» (`act`): исходящие сотрудника; `calls` — число из таблицы. */
  activity?: { user: number | null; user_name: string | null; kind: string; step: string; calls: number };
  calls: CallItem[];
};

/** `none` без правок, `any` есть правки, `stage`/`nostage` стадия менялась/нет; `to` — куда перевели (`deal:<стадия>`). */
export type ChangeSummary = { none: number; any: number; stage: number; nostage: number; to: { value: string; calls: number }[] };

export type ListParams = {
  from: string;
  to: string;
  type?: string;
  user?: string;
  missed?: string;
  talk?: string;
  change?: string;
  /** День обзвона: только звонки по сделкам его когорты; `dtype` — один тип обзвона. */
  dialing?: string;
  dtype?: string;
  tag?: string;
  /** Только разговоры, после которых записали на примерку: способ и строка рейтинга «Записей». */
  zapis?: string;
  zuser?: string;
  /** Исходящие сотрудника из «Активности»: вид (lead|deal|rest|out), userID CRM и шаг (talk|zapis). */
  act?: string;
  auser?: string;
  astep?: string;
  /** Слово (или слова через запятую) целиком в расшифровке разговора. */
  q?: string;
  page?: string;
};

export function loadCallsList(p: ListParams): Promise<CallsList> {
  return apiFetch<CallsList>(`/api/calls/list${query(p)}`);
}

/** Какие слова звучали в разговорах выборки — `GET /api/calls/words` (вкладка «Звонки»). */
export type CallWord = { word: string; calls: number; hits: number };
export type CallWords = {
  from: string;
  to: string;
  /** Сколько расшифровок прочитали и упёрлись ли в потолок. */
  talks: number;
  limit: number;
  capped: boolean;
  words: CallWord[];
  pairs: CallWord[];
};

/** Фильтры выборки; пустые значения `query` отбрасывает сам. */
export type WordsParams = { from: string; to: string; type?: string; user?: string; missed?: string; talk?: string; tag?: string; q?: string };

export function loadCallWords(p: WordsParams): Promise<CallWords> {
  return apiFetch<CallWords>(`/api/calls/words${query(p)}`);
}

/** Карточка звонка `/calls/<id>` — `GET /api/calls/{id}`. */
export function loadCall(id: number): Promise<CallItem> {
  return apiFetch<CallItem>(`/api/calls/${id}`);
}

export type ObjectKind = "lead" | "deal";
export type ObjectCalls = { kind: ObjectKind; id: number; lead_ids: number[]; phones: string[]; limit: number; calls: CallItem[] };

export function loadObjectCalls(kind: ObjectKind, id: number): Promise<ObjectCalls> {
  return apiFetch<ObjectCalls>(`/api/calls/by-object${query({ kind, id: String(id) })}`);
}

/** Попытка — отдельный «день + час» со звонком (входящие тоже), как `call_num_all` Резерва. */
export type AttemptsDay = { date: string; attempts: number; calls: number; talks: number };
/** Автоотмена лида Резервом: попыток ≥ `attempts` и не раньше `from_date`; `due` — оба условия уже есть. */
export type AttemptsCancel = { attempts: number; attempts_left: number; from_date: string; due: boolean };
export type CallAttempts = {
  kind: ObjectKind;
  id: number;
  created_at: string;
  /** С какого момента считаем: лид — с создания; сделка — с начала дня создания её лида. */
  since: string;
  age_days: number;
  attempts: number;
  calls: number;
  outgoing: number;
  talks: number;
  days: number;
  last_at: string | null;
  by_day: AttemptsDay[];
  /** У сделки автоотмены нет — `null`. */
  cancel: AttemptsCancel | null;
};

/** Плашка «Чатов» над полем ответа — `GET /api/calls/attempts`. */
export function loadCallAttempts(kind: ObjectKind, id: number): Promise<CallAttempts> {
  return apiFetch<CallAttempts>(`/api/calls/attempts${query({ kind, id: String(id) })}`);
}

export type CallTagCount = { name: string; calls: number };

export function loadCallTags(): Promise<CallTagCount[]> {
  return apiFetch<CallTagCount[]>("/api/calls/tags");
}

/** Поставить тег звонку; ответ — теги звонка. */
export function addCallTag(id: number, name: string): Promise<string[]> {
  return apiFetch<string[]>(`/api/calls/${id}/tags`, { method: "POST", body: JSON.stringify({ name }) });
}

export function removeCallTag(id: number, name: string): Promise<string[]> {
  return apiFetch<string[]>(`/api/calls/${id}/tags${query({ name })}`, { method: "DELETE" });
}
